import { Component, OnDestroy, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, iif, merge, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeScan, share, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import MicroModal from 'micromodal';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { selectedGroupMemberUuids, selectHasMembers, unselectedMemberUuid } from '../../services/lead-seller-group/reducer';
import { LeadSellerPreferencesService } from '../../services/lead-seller-preferences/lead-seller-preferences.service';
import { DealerGroupsState } from '../../interfaces/dealer-group.interface';
import {
  LaunchDarklyService,
  UserService,
  StatusFilter,
  SellConfigurationState,
  UpdateFilterStatus,
  searchParamsFromSellSort,
  DateUtilsService,
  ResetFilters,
  SetStatusFilter,
  ClearStockNumberList,
  ClearVinList,
  DoSearch,
  SearchAuctionItems,
  SellSearchState,
  UpdateLeadSellerPayload,
  SearchFiltersState,
  TenantFilter,
  BackLinkState, ConfigurationState, SearchOptionModel, UpdateSelectedSearchMode, SetGroundedStatusFilter, GroundedStatusFilters,
  UpdateReservedByUsers,
  SetReservedByUsers,
  UpdateReservedForDealers,
  SetReservedForDealers
} from 'remarketing-angular-library';
import { showHomePageBackLink } from 'src/app/shared/utils/util';
import { AnalyticsService, BackLinkComponent, fromMatchMedia, FromMatchMediaEvent } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { DealerGroupComponent } from '../dealer-group/dealer-group.component';
import { TrackDirective, TrapFocusComponent } from 'remarketing-ui-components-lib';
import { AdvancedSearchHeaderComponent } from '../advanced-search';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { PreferencesPopoverComponent } from '../preferences-popover/preferences-popover.component';
import { MultiVinModalComponent } from '../multi-vin-modal/multi-vin-modal.component';


function StockNumLengthValidator(control: AbstractControl) {
  const stockNum = control.value.trim().replace(/[\s,]+/gi, '');
  if (stockNum.length > 12) {
    return true;
  }
  return null;
}

export const enum SellSearchType {
  VIN = 'VIN',
  STOCK_NUMBER = 'Stock Number',
  SELLER_LEGACY_ID = 'sellerLegacyId',
  BLANK = 'Blank'
}

interface SelectedSearchModeModel {
  selectedSearchMode: SearchOptionModel;
}

export function desktopSearchType(vin: string, stockNumber: string, sellerLegacyId: number): SellSearchType {
  if (vin && vin.length > 0) {
    return SellSearchType.VIN;
  }
  if (stockNumber && stockNumber.length > 0) {
    return SellSearchType.STOCK_NUMBER;
  }
  if (sellerLegacyId && sellerLegacyId.toString().length == 6) {
    return SellSearchType.SELLER_LEGACY_ID;
  }
  return SellSearchType.BLANK;
}

export function mobileSearchType(
  vin: string,
  stockNumber: string,
  sellerLegacyId: number,
  mobileSearch: 'vin' | 'stock' | 'sellerLegacyId'
): SellSearchType {
  if (vin && vin.length > 0 && mobileSearch === 'vin') {
    return SellSearchType.VIN;
  }
  if (stockNumber && stockNumber.length > 0 && mobileSearch === 'stock') {
    return SellSearchType.STOCK_NUMBER;
  }
  if (sellerLegacyId && sellerLegacyId.toString().length == 6 && mobileSearch === 'sellerLegacyId') {
    return SellSearchType.SELLER_LEGACY_ID;
  }
  return SellSearchType.BLANK;
}

@Component({
    selector: 'lib-manage-header',
    templateUrl: './manage-header.component.html',
    styleUrls: ['./manage-header.component.scss'],
    imports: [
        TranslocoDirective,
        CommonModule,
        BackLinkComponent,
        TabsModule,
        ReactiveFormsModule,
        DealerGroupComponent,
        TrackDirective,
        AdvancedSearchHeaderComponent,
        TrapFocusComponent,
        PopoverModule,
        PreferencesPopoverComponent,
        MultiVinModalComponent
    ]
})
export class ManageHeaderComponent implements OnInit, OnDestroy {
  activeTab: string;
  searchForm: UntypedFormGroup;
  private storedSearchMode = this.store.selectSnapshot(SellSearchState.selectedSearchMode) || 'vin';
  private mobileSearchMode$$ = new BehaviorSubject<SearchOptionModel>(this.storedSearchMode);
  get mobileSearchMode(): SearchOptionModel {
    return this.mobileSearchMode$$.getValue();
  }
  set mobileSearchMode(mode) {
    this.mobileSearchMode$$.next(mode);
  }
  changeSearchMode(newSearchMode: SearchOptionModel) {
    this.analyticsTrackSearchMode(newSearchMode);
    this.store.dispatch(new UpdateSelectedSearchMode(newSearchMode));
  }

  analyticsTrackSearchMode(newSearchMode: SearchOptionModel) {
    let searchMode;
    if (this.inServiceVehiclesEnabled && newSearchMode === 'stock') {
      searchMode = 'mva';
    } else {
      searchMode = newSearchMode;
    }
    this._analyticsService.trackEvent('radioButton', searchMode);
  }

  isDesktop$ = fromMatchMedia('(min-width: 769px)');
  searchTrackName$: Observable<string>;

  private destroyed$ = new Subject();

  isDesktop = new BehaviorSubject<FromMatchMediaEvent>(null);

  hasLeadSellerMembers$ = this.leadSellerGroupService.select(selectHasMembers);
  hasPreferencesMembers$ = this.leadSellerPreferencesService.select(selectHasMembers);

  disableFocus = false;

  @ViewChild('pop') preferencePopover;

  preferencesPreState: DealerGroupsState;

  userProfile;
  showBackLink: boolean;

  @Output() resetTenant = new EventEmitter<boolean>();

  get isSellerAdvancedSearchEnabled() {
    return this._launchDarklyService.getFeatureFlag('sell-advancedSearchEnabled', false);
  }
  get isSellerLegacyIdSearchEnabled() {
    return this._launchDarklyService.getFeatureFlag('sell-sellerLegacyIdSearchEnabled', false);
  }

  get isAdmin() {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  get inServiceVehiclesEnabled() {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store: Store,
    readonly leadSellerGroupService: LeadSellerGroupService,
    readonly leadSellerPreferencesService: LeadSellerPreferencesService,
    private userService: UserService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly dateUtilsService: DateUtilsService,
    private readonly _analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.homePageBackLink();
    this.userLeadSellerPreferences();

    this.searchForm = this.formBuilder.group({
      vin: ['', [Validators.minLength(6), Validators.maxLength(17)]],
      stockNum: ['', [StockNumLengthValidator]],
      sellerLegacyId: [null, [Validators.minLength(6), Validators.maxLength(6)]]
    });

    const vinCtrl = this.searchForm.get('vin');
    const stockNumCtrl = this.searchForm.get('stockNum');
    const sellerLegacyIdCtrl = this.searchForm.get('sellerLegacyId');

    this.mobileSearchMode$$.next(this.store.selectSnapshot(SellSearchState.selectedSearchMode));

    this.store
      .select(SellSearchState.searchRequest)
      .pipe(
        takeUntil(this.destroyed$),
        filter(req => !!req)
      )
      .subscribe(({ vin = '', stockNumber = '', sellerLegacyId = null }) => {
        vinCtrl.setValue(vin);
        stockNumCtrl.setValue(stockNumber);
        sellerLegacyIdCtrl.setValue(sellerLegacyId);
      });
    const formattedVin$ = vinCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      map<string, string>(val => val.trim()),
      mergeScan((lastGoodValue, value) => {
        if (value.length > 17 && lastGoodValue.length === 0) {
          return of(value.slice(0, 17));
        }
        if (value.length > 17) {
          return of(lastGoodValue);
        }
        return of(value);
      }, ''),
      tap(val => {
        vinCtrl.setValue(val);
      })
    );
    const formattedStockNum$: Observable<string> = stockNumCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      map(val => {
        const trimmedStockValue = val.trim().replace(/[\s,]/gi, '');
        const spaceCount = val.split(' ').length - 1;
        if (trimmedStockValue.length >= 12) {
          return val.slice(0, 12 + spaceCount).trim();
        }
        return val;
      }),
      tap(val => {
        stockNumCtrl.setValue(val);
      })
    );
    const formattedSellerLegacyId$: Observable<number> = sellerLegacyIdCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      map<number, number>(val => val?.valueOf()),
      tap(val => {
        sellerLegacyIdCtrl.setValue(val);
      })
    );
    merge(formattedVin$, formattedStockNum$, formattedSellerLegacyId$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
    this.isDesktop$.pipe(takeUntil(this.destroyed$)).subscribe(matches => this.isDesktop.next(matches));
    // Dynamically set the `data-track-name` for analytics
    this.searchTrackName$ = this.isDesktop$.pipe(
      map(({ matches }) => matches),
      switchMap(isDesktop => {
        // Whenever the UI switches between mobile & desktop recalculate the data track name based on the
        // values in VIN, Stock #, and optionally the mobile search option
        const searchValues$ = combineLatest(
          formattedVin$.pipe(startWith('')),
          formattedStockNum$.pipe(startWith('')),
          formattedSellerLegacyId$.pipe(startWith(Number.NaN)),
          this.mobileSearchMode$$.asObservable()
        );
        return iif(
          () => isDesktop,
          searchValues$.pipe(
            map(([vin, stockNumber, sellerLegacyId]) => desktopSearchType(vin, stockNumber, sellerLegacyId))
          ),
          searchValues$.pipe(
            map(([vin, stockNumber, sellerLegacyId, mobileSearchMode]) =>
              mobileSearchType(vin, stockNumber, sellerLegacyId, mobileSearchMode)
            )
          )
        );
      }),
      map(searchType => `${searchType} - search`),
      share()
    );

    this.advancedSearchTabAnalytics();
  }

  homePageBackLink() {
    this.store
      .select(BackLinkState.backLinkState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(state => {
        this.showBackLink = showHomePageBackLink(state);
      });
  }

  analyticsFormSuccess(vin, stockNumber) {
    this._analyticsService.track(
      'customEvent',
      this._analyticsService.formSubmit('formSuccess', 'formValidationSuccess', 'searchForm', [
        {
          track: 'yes',
          name: 'searchVinInput',
          value: vin !== '' ? vin : null,
          isPII: 'false'
        },
        {
          track: 'yes',
          name: this.analyticsMvaOrStockReference(),
          value: stockNumber !== '' ? stockNumber : null,
          isPII: 'false'
        }
      ])
    );
  }

  analyticsMvaOrStockReference() {
    return this.inServiceVehiclesEnabled ? 'searchMvaNumInput' : 'searchStockNumInput';
  }

  search() {
    const currentState = this.leadSellerGroupService.snapShot();

    const vin = this.searchForm.get('vin').value;
    const stockNumber = this.searchForm.get('stockNum').value.trim();
    let sellerLegacyId = this.searchForm.get('sellerLegacyId').value;
    const isBlankSearch = vin === '' && stockNumber === '' && sellerLegacyId === null;
    if (this.searchForm.valid) {
      let {
        sortBy,
        sortOrder,
        bsoBuyerOeUuid,
        filterByStatus,
        filterByInService,
        grounded
      } = this.store.selectSnapshot(SellSearchState.searchRequest);
      if (!vin && !stockNumber) {
        // reset ground status to ALL if either of vin or stock number is not used for search
        this.store.dispatch([new SetGroundedStatusFilter(GroundedStatusFilters.ALL)]);
        grounded = null;
      }
      const { tenantFilter } = this.store.selectSnapshot(SearchFiltersState.searchFiltersState) || {};

      // If PCS feature is enabled, default search to all vehicles
      const sellUpdateInventory = this._launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
      const statusFilter = sellUpdateInventory ? null : filterByStatus;

      const defaultFromDate =
        statusFilter === StatusFilter.SOLD_VEHICLES ? this.dateUtilsService.DEFAULT_DATES.fromDate : undefined;
      const sort =
        statusFilter === StatusFilter.SOLD_VEHICLES
          ? { sortBy, sortOrder }
          : searchParamsFromSellSort(this.store.selectSnapshot(SellConfigurationState.allSortConfig));
      const dispatchSearch = this.generateDispatchSearch(
        statusFilter,
        bsoBuyerOeUuid,
        currentState,
        sort,
        sellUpdateInventory,
        filterByInService,
        grounded
      );
      this.analyticsFormSuccess(vin, stockNumber);
      this.resetReservationFilter();
      // update tenant filter to all (Event Emit)
      if (tenantFilter !== TenantFilter.ALL) {
        this.resetTenant.emit(true);
      }
      if (!this.isSellerLegacyIdSearchEnabled) {
        sellerLegacyId = null;
      }
      this.processDispatchSearch(dispatchSearch, vin, stockNumber, sellerLegacyId, isBlankSearch, defaultFromDate);
    } else {
      this._analyticsService.track(
        'customEvent',
        this._analyticsService.formSubmit(
          'formError',
          'formValidationError',
          'searchForm',
          [
            {
              track: 'yes',
              name: 'searchVinInput',
              value: vin !== '' ? vin : null,
              isPII: 'false'
            },
            {
              track: 'yes',
              name: this.analyticsMvaOrStockReference(),
              value: stockNumber !== '' ? stockNumber : null,
              isPII: 'false'
            },
            {
              track: 'yes',
              name: 'searchSellerLegacyIdInput',
              value: sellerLegacyId !== null ? sellerLegacyId : null,
              isPII: 'false'
            }
          ],
          'Please enter a valid VIN.'
        )
      );
    }
  }

  // Generates the proper dispatchSearch function for a given payload
  generateDispatchSearch(
    filterByStatus,
    bsoBuyerOeUuid,
    currentState,
    sort,
    sellUpdateInventory = false,
    filterByInService = false,
    grounded = null
  ): any {
    let dispatchSearch;
    if (sellUpdateInventory) {
      dispatchSearch = payload =>
        this.store.dispatch([
          new SetStatusFilter(StatusFilter.ALL_VEHICLES),
          new UpdateFilterStatus(StatusFilter.ALL_VEHICLES),
          new ClearVinList(),
          new ClearStockNumberList(),
          new ResetFilters(),
          new SearchAuctionItems({
            leadSellerGroupMembers: selectedGroupMemberUuids(currentState),
            page: 0,
            bsoBuyerOeUuid,
            filterByStatus,
            filterByInService,
            grounded,
            tenantUuid: '',
            ...sort,
            ...payload
          })
        ]);
    } else {
      dispatchSearch = payload =>
        this.store.dispatch([
          new ClearVinList(),
          new ClearStockNumberList(),
          new ResetFilters(),
          new SearchAuctionItems({
            leadSellerGroupMembers: selectedGroupMemberUuids(currentState),
            page: 0,
            bsoBuyerOeUuid,
            filterByStatus,
            filterByInService,
            grounded,
            tenantUuid: '',
            ...sort,
            ...payload
          })
        ]);
    }
    return dispatchSearch;
  }

  // Processes a given dispatchSearch function based on Mobile vs Desktop search mode
  processDispatchSearch(dispatchSearch, vin, stockNumber, sellerLegacyId, isBlankSearch, defaultFromDate) {
    // desktop search
    if (this.isDesktop.getValue().matches) {
      dispatchSearch({
        vin,
        stockNumber,
        sellerLegacyId,
        fromDate: isBlankSearch ? defaultFromDate : undefined,
        toDate: isBlankSearch ? this.dateUtilsService.DEFAULT_DATES.toDate : undefined
      });
    } else if (this.mobileSearchMode === 'vin') {
      dispatchSearch({
        vin,
        fromDate: vin === '' ? defaultFromDate : undefined,
        toDate: vin === '' ? this.dateUtilsService.DEFAULT_DATES.toDate : undefined
      });
    } else if (this.mobileSearchMode === 'stock') {
      dispatchSearch({
        stockNumber,
        fromDate: stockNumber === '' ? defaultFromDate : undefined,
        toDate: stockNumber === '' ? this.dateUtilsService.DEFAULT_DATES.toDate : undefined
      });
    } else if (this.mobileSearchMode === 'sellerLegacyId') {
      dispatchSearch({
        sellerLegacyId,
        fromDate: sellerLegacyId === null ? defaultFromDate : undefined,
        toDate: sellerLegacyId === null ? this.dateUtilsService.DEFAULT_DATES.toDate : undefined
      });
    }
  }

  advancedSearchTabAnalytics() {
    // set attributes for bootstrap tab header a tag, adding attributes for analytics
    setTimeout(() => {
      const parentTab = document.querySelector('div.advanced_search > tabset.tab-container');
      const target = parentTab.querySelectorAll('a.nav-link');
      const tabUl = parentTab.querySelector('ul');
      const navItem = parentTab.querySelectorAll('ul li');
      const navLink = parentTab.querySelectorAll('ul li a');

      this.parentTabExists(parentTab, target, tabUl, navItem, navLink);
    }, 500);
  }

  parentTabExists(parentTab, target, tabUl, navItem, navLink) {
    if (parentTab) {
      target[0].setAttribute('data-track-elem', 'tab');
      target[0].setAttribute('data-track-name', 'Search');
      target[0].setAttribute('data-track-trigger', 'switch');

      target[1].setAttribute('data-track-elem', 'tab');
      target[1].setAttribute('data-track-name', 'Advanced Search');
      target[1].setAttribute('data-track-trigger', 'switch');

      tabUl.setAttribute('role', 'list');

      navItem.forEach(item => {
        item.setAttribute('role', 'listitem');
      });

      navLink.forEach(item => {
        item.removeAttribute('role');
        item.removeAttribute('aria-selected');
      });
    }
  }

  openMicroModal(modalId: string) {
    MicroModal.show(modalId, {
      disableScroll: true
    });
    const heading = document.querySelector(`#${modalId} h1`) as HTMLElement;
    if (heading) {
      heading.focus();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onShowPreferencesPop() {
    this.leadSellerPreferencesService.state$.pipe(take(1)).subscribe(state => (this.preferencesPreState = state));
  }

  onHidePreferencesPop() {
    if (this.preferencesPreState !== null) {
      this.leadSellerPreferencesService.setState(this.preferencesPreState);
    }
  }

  savePreferences(): void {
    this.preferencesPreState = null;

    this.preferencePopover.hide();

    const currentState = this.leadSellerPreferencesService.snapShot();

    this.store.dispatch([new UpdateLeadSellerPayload(selectedGroupMemberUuids(currentState)), new DoSearch()]);
    this.leadSellerGroupService.setState(currentState);
    this.leadSellerPreferencesService.setState(currentState);
    this.leadSellerGroupService.cacheState();

    this.updateUserPreferences(currentState);
  }

  userLeadSellerPreferences() {
    this.store
      .select(state => state.user.userProfile)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(profile => {
        if (profile) {
          this.userProfile = profile;
        }
        const leadSellerExcludedDealers =
          this.userProfile && this.userProfile.userPreferences.leadSellerExcludedDealers
            ? this.userProfile.userPreferences.leadSellerExcludedDealers
            : [];
        this.leadSellerGroupService.loadLeadSellerGroups(leadSellerExcludedDealers);
      });
  }

  updateUserPreferences(state) {
    const leadSellerExcludedDealers = unselectedMemberUuid(state);

    const payload = {
      ...this.userProfile,
      userPreferences: {
        ...this.userProfile.userPreferences,
        leadSellerExcludedDealers: leadSellerExcludedDealers.length === 0 ? null : leadSellerExcludedDealers
      }
    };

    this.userService.updateUser(this.userProfile.userUuid, payload).subscribe(
      updatedUser => {
        const currentState = this.store.selectSnapshot(state => state);
        this.store.reset({
          ...currentState,
          user: {
            ...currentState.user,
            userProfile: {
              ...this.userProfile,
              userPreferences: {
                ...this.userProfile.userPreferences,
                leadSellerExcludedDealers: updatedUser.userPreferences.leadSellerExcludedDealers
              }
            }
          }
        });
      },
      error => {
        console.error('error: ', error);
      }
    );
  }

  resetReservationFilter() {
    this.store.dispatch([
      new UpdateReservedByUsers(null),
      new SetReservedByUsers(null),
      new UpdateReservedForDealers(null),
      new SetReservedForDealers(null)
    ]);
  }
}
